/**
 * ShutdownEvidenceChip (NC-1289)
 *
 * Compact answer to the top beta question — "wurde die VM auch abgeschaltet?" —
 * so it no longer requires a dive into the Diagnostics tab. Used in the
 * RunDetails header and in the RunsPage table.
 *
 * All classification lives in utils/runShutdownEvidence.ts (pure + unit-tested);
 * this component only renders it. `liveVms` is optional on purpose: nothing
 * fetches a power-state snapshot for the sake of this badge, because that would
 * add an ARM-backed call to every run-detail page view.
 */

import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import { Run } from '../services/types';
import {
  resolveShutdownEvidence,
  LiveVmState,
  ShutdownEvidence,
} from '../utils/runShutdownEvidence';
import { formatDateSafe } from '../utils/dateFormat';

interface ShutdownEvidenceChipProps {
  run: Run;
  liveVms?: LiveVmState[];
  size?: 'small' | 'medium';
}

/** "VMs gestoppt" + " seit <date>" when a timestamp backs the state. */
export function evidenceChipLabel(evidence: ShutdownEvidence): string {
  if (!evidence.at) return evidence.label;
  const when = formatDateSafe(evidence.at);
  const preposition = evidence.state === 'stopped' ? 'seit' : 'am';
  return `${evidence.label} ${preposition} ${when}`;
}

export const ShutdownEvidenceChip: React.FC<ShutdownEvidenceChipProps> = ({
  run,
  liveVms,
  size = 'small',
}) => {
  const evidence = resolveShutdownEvidence(run, liveVms);

  return (
    <Tooltip title={evidence.tooltip}>
      <Chip
        label={evidenceChipLabel(evidence)}
        color={evidence.color}
        size={size}
        variant={evidence.color === 'default' ? 'outlined' : 'filled'}
        data-testid={`shutdown-evidence-${evidence.state}`}
      />
    </Tooltip>
  );
};

export default ShutdownEvidenceChip;
